"use client";

import React, { useState, useCallback } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { FileText, Loader2, Upload, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { a2pService } from "@/app/_module/services/a2pService";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { Notice, Panel, Section, StatusLabel, focusRing } from "./overview/primitives";
import { Choice, ChoiceGroup, StepIntro } from "./overview/wizard-ui";

interface StepCp575UploadProps {
  onUploadComplete?: (uploadId: string) => void;
}

export default function StepCp575Upload({ onUploadComplete }: StepCp575UploadProps) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { branding } = useBranding();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const einIsNew = watch("einIsNew");
  // Coming back to this step remounts it; the upload itself is still in the form.
  const uploadedEarlier = !uploadedFile && !!watch("cp575UploadId");

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Upload a PDF, JPG or PNG file.");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("That file is over 10 MB. Upload a smaller copy.");
      return;
    }

    setUploadedFile(file);
    setUploadError(null);
    setIsUploading(true);

    try {
      const result = await a2pService.uploadCp575Document(file);
      setUploadId(result.uploadId);
      setValue("cp575UploadId", result.uploadId);
      if (onUploadComplete) {
        onUploadComplete(result.uploadId);
      }
    } catch (error: any) {
      setUploadError(error.message || "The upload didn't go through. Try again.");
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  }, [setValue, onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  const removeFile = () => {
    setUploadedFile(null);
    setUploadId(null);
    setValue("cp575UploadId", undefined);
    setUploadError(null);
  };

  const supportLink = (
    <a href={`mailto:${branding.supportEmail}`} className={cn("font-medium underline underline-offset-4", focusRing)}>
      {branding.supportEmail}
    </a>
  );

  return (
    <div className="space-y-8">
      <StepIntro title="Your CP 575 letter">
        <p>
          The CP 575 is the letter the IRS sends when it issues your EIN. It shows the EIN and the date it was issued.
          If you applied online, the confirmation you saved at the end is your CP 575.
        </p>
      </StepIntro>

      <Section id="a2p-ein-age" title="Is your EIN at least 45 days old?" description="Check the issue date on the letter.">
        <Controller
          name="einAgeConfirmed"
          control={control}
          rules={{ required: "Tell us whether your EIN is at least 45 days old" }}
          render={({ field }) => (
            <ChoiceGroup
              aria-labelledby="a2p-ein-age"
              value={field.value === true ? "yes" : field.value === false ? "no" : ""}
              onValueChange={(value) => {
                const isConfirmed = value === "yes";
                field.onChange(isConfirmed);
                setValue("einIsNew", !isConfirmed);
              }}
            >
              <Choice
                value="yes"
                title="Yes, it's 45 days or older"
                description="The issue date on my CP 575 is more than 45 days ago."
              />
              <Choice
                value="no"
                title="No, it's newer than 45 days"
                description="It was issued in the last 45 days. Our team reviews these by hand before they go to the carriers."
              />
            </ChoiceGroup>
          )}
        />
        {errors.einAgeConfirmed && <p className="text-sm text-red-700">{errors.einAgeConfirmed.message as string}</p>}
        {einIsNew && (
          <Notice>
            <strong>New EIN.</strong> Our team checks your CP 575 and reviews the application by hand before it goes to
            the carriers, and we&apos;ll contact you if we need anything. Questions? Email {supportLink}.
          </Notice>
        )}
      </Section>

      <Section
        id="a2p-cp575"
        title="Upload the letter"
        description="PDF, JPG or PNG, up to 10 MB. We delete it once your registration is approved."
      >
        {uploadedFile || uploadedEarlier ? (
          <Panel className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <FileText aria-hidden className="size-5 shrink-0 text-neutral-500" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-neutral-900">
                  {uploadedFile ? uploadedFile.name : "CP 575 letter"}
                </p>
                <p className="mt-0.5 text-xs tabular-nums text-neutral-500">
                  {uploadedFile ? `${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB` : "Uploaded earlier"}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {isUploading ? (
                <StatusLabel tone="waiting">Uploading</StatusLabel>
              ) : uploadId || uploadedEarlier ? (
                <StatusLabel tone="ok">Uploaded</StatusLabel>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={removeFile}
                disabled={isUploading}
                aria-label="Remove the file"
                className={cn("size-11 sm:size-9", focusRing)}
              >
                <X />
              </Button>
            </div>
          </Panel>
        ) : (
          <div
            {...getRootProps()}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed bg-white px-6 py-10 text-center transition-colors",
              isDragActive ? "border-primary bg-primary/[0.03]" : "border-neutral-400 hover:border-neutral-500 hover:bg-neutral-50",
              isUploading && "cursor-wait opacity-60",
              focusRing,
            )}
          >
            <input {...getInputProps()} aria-label="Upload your CP 575 letter" />
            {isUploading ? (
              <Loader2 aria-hidden className="size-6 animate-spin text-neutral-500" />
            ) : (
              <Upload aria-hidden className="size-6 text-neutral-500" />
            )}
            <p className="mt-3 text-sm font-medium text-neutral-900">
              {isUploading ? "Uploading…" : isDragActive ? "Drop it here" : "Drop your CP 575 here, or choose a file"}
            </p>
            <p className="mt-1 text-xs text-neutral-500">PDF, JPG or PNG, up to 10 MB</p>
          </div>
        )}

        {uploadError && <Notice tone="blocked">{uploadError}</Notice>}

        {/* Hidden field for form validation */}
        <Controller
          name="cp575UploadId"
          control={control}
          rules={{ required: "Upload your CP 575 letter" }}
          render={({ field }) => <input type="hidden" {...field} value={field.value || ""} />}
        />
        {errors.cp575UploadId && <p className="text-sm text-red-700">{errors.cp575UploadId.message as string}</p>}
      </Section>

      <div className="space-y-2 text-pretty text-sm leading-relaxed text-neutral-500">
        <p>
          Can&apos;t find it? If you applied online, look for the letter you saved at the end of the application.
          Otherwise ask the IRS for a copy on the Business and Specialty Tax Line, 1-800-829-4933.
        </p>
        <p>Questions about registering? Email {supportLink}.</p>
      </div>
    </div>
  );
}
